import Button from "@components/ui/buttons/Button";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useEffect, useState } from "react";
import DataGrid from "@components/ui/tables/DataGrid";
import { transformPartnersToTable } from "@components/ui/tables/transformToTables";
import { useProjectStore } from "@store/projectStore";
import { CreatePartnerModal } from "@components/modals/CreatePartnerModal";
import { ModifyPartnerModal } from "@components/modals/ModifyPartnerModal";
import PartnerWorkloadModal from "@components/modals/PartnerWorkloadModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { PartnerInfos } from "types";
import { showNotification } from "context/notification.service";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import { colors } from "sexy";
import { ChargeArtisanTable } from "@components/ui/blocks/ChargeArtisanTable";
import { useDataStore } from "@store/user_org";
import { searchPartnersFull } from "@services/algolia";
import { partnersApi } from "@services/api/partners";
import { ManageDecennalesModal } from "@components/modals/ManageDecennalesModal";
import { BulkEditDecennalesModal } from "@components/modals/BulkEditDecennalesModal";
import { HIDDEN_COMPANY_TYPES, COMPANY_TYPE_OPTIONS } from "@constants/partnerTypes";
import { isOwnerOrAdmin } from "@utils/permissions";

const mainTabs = [
  { value: "list", label: "Artisans" },
  { value: "charge", label: "Charge artisan" },
];

const typeTabs = [
  { value: "all", label: "Tous" },
  ...COMPANY_TYPE_OPTIONS.filter((o) => o.value !== "autre"),
  { value: "autre", label: "Autre" },
  { value: "sans_decennale", label: "Sans décennale" },
];

export default function PartnersScreen() {
  const partnersList = useProjectStore((state) => state.partnersList);
  const hasMorePartnersList = useProjectStore((state) => state.hasMorePartnersList);
  const hasPrevPartnersList = useProjectStore((state) => state.hasPrevPartnersList);
  const fetchPartnersList = useProjectStore((state) => state.fetchPartnersList);
  const fetchNextPagePartners = useProjectStore((state) => state.fetchNextPagePartners);
  const fetchPrevPagePartners = useProjectStore((state) => state.fetchPrevPagePartners);
  const setPartnersSort = useProjectStore((state) => state.setPartnersSort);
  const removePartners = useProjectStore((state) => state.removePartners);
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const canDelete = isOwnerOrAdmin(userData);
  const [table, setTable] = useState<any>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [modifyPartner, setModifyPartner] = useState<PartnerInfos | null>(null);
  const [workloadPartner, setWorkloadPartner] = useState<PartnerInfos | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const urlTab = new URLSearchParams(window.location.search).get("tab");
  const [activeTab, setActiveTab] = useState<string>(urlTab || "all");
  const [activeMainTab, setActiveMainTab] = useState<string>("list");
  const [decennaleTarget, setDecennaleTarget] = useState<{ id: string; name: string } | null>(null);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [chargeInitialFilter, setChargeInitialFilter] = useState<string>("");

  useEffect(() => {
    if (orgData?.id) {
      // "sans_decennale" is a client-side filter — fetch all partners
      fetchPartnersList(orgData.id, activeTab === "sans_decennale" ? "all" : activeTab);
    }
  }, [orgData?.id, activeTab]);

  useEffect(() => {
    let list = [...partnersList];
    if (activeTab === "sans_decennale") {
      list = list.filter((p) => !p.decennales || p.decennales.length === 0);
    }
    // Alphabetical by visible label. Fall back to display_name when
    // company_name is empty so those rows don't all clump at the top.
    //
    // French collation (via localeCompare) sorts digits AFTER letters —
    // that's the CLDR rule. Group names that start with a digit first,
    // then letter-starting names, and use fr collation inside each group
    // so accented characters still sort correctly.
    const getSortLabel = (p: typeof partnersList[number]) =>
      (p.company_name || p.display_name || "").trim().toLowerCase();
    const groupOf = (s: string) => (/^\d/.test(s) ? 0 : 1);
    const sorted = list.sort((a, b) => {
      const la = getSortLabel(a);
      const lb = getSortLabel(b);
      const ga = groupOf(la);
      const gb = groupOf(lb);
      if (ga !== gb) return ga - gb;
      return la.localeCompare(lb, "fr", {
        sensitivity: "base",
        numeric: true,
      });
    });
    const table = transformPartnersToTable(
      sorted,
      "all",
      "",
      (partnerId, partnerName) => {
        setDecennaleTarget({ id: partnerId, name: partnerName });
      },
      (_partnerId, partnerName) => {
        setChargeInitialFilter(partnerName);
        setActiveMainTab("charge");
      },
    );
    setTable(table);
  }, [partnersList, activeTab]);

  const handleRowClick = (row: any) => {
    const partner = partnersList.find((p) => p.id === row.id);
    if (partner) {
      setWorkloadPartner(partner);
    }
  };

  const handleSearchSelect = async (option: any) => {
    try {
      const partner = await partnersApi.get(option.value);
      setWorkloadPartner(partner);
    } catch {
      showNotification({ type: "error", message: "Impossible de charger l'intervenant" });
    }
  };

  const handleDeleteSelected = () => {
    removePartners(selectedIds);
    showNotification({
      type: "success",
      message: `${selectedIds.length} intervenant(s) supprimé(s)`,
    });
    setSelectedIds([]);
    setShowDeleteConfirm(false);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {createModalOpen && (
        <CreatePartnerModal onClose={() => setCreateModalOpen(false)} />
      )}
      {modifyPartner && (
        <ModifyPartnerModal
          onClose={() => setModifyPartner(null)}
          initialPartner={modifyPartner}
        />
      )}
      {workloadPartner && (
        <PartnerWorkloadModal
          partner={workloadPartner}
          onClose={() => setWorkloadPartner(null)}
          onEdit={() => {
            setModifyPartner(workloadPartner);
            setWorkloadPartner(null);
          }}
        />
      )}
      {decennaleTarget && (
        <ManageDecennalesModal
          partnerId={decennaleTarget.id}
          partnerName={decennaleTarget.name}
          onClose={() => setDecennaleTarget(null)}
        />
      )}
      {showBulkEdit && orgData?.id && (
        <BulkEditDecennalesModal
          orgId={orgData.id}
          onClose={() => setShowBulkEdit(false)}
        />
      )}
      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer ${selectedIds.length} intervenant(s) ?`}
          message="Cette action est irréversible. Tous les intervenants sélectionnés seront supprimés définitivement."
          confirmText="CONFIRMER"
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteSelected}
        />
      )}
      <div className="flex-shrink-0 pt-10 px-4 lg:px-10 pb-4">
        <div className="flex flex-col md:flex-row w-full max-w-1p2k justify-between">
          <div className="flex flex-col w-full md:w-400">
            <Text1>Artisans</Text1>
            <Text2>Retrouvez tous vos artisans ici.</Text2>
            <Text3>
              Les intervenants sont les entreprises et contacts qui
              participent à vos projets.
            </Text3>
          </div>
          {activeMainTab === "list" && (
            <div className="flex flex-row gap-2 items-start">
              {selectedIds.length > 0 && canDelete && (
                <SecondaryButton
                  danger={true}
                  startIcon={
                    <Icon1 name="delete" size={22} color={colors.red_500} />
                  }
                  label={`Supprimer la sélection (${selectedIds.length})`}
                  variant="large"
                  onClick={() => setShowDeleteConfirm(true)}
                />
              )}
              <SecondaryButton
                startIcon={<Icon1 name="table" size={18} />}
                label="Éditer décennales"
                variant="large"
                onClick={() => setShowBulkEdit(true)}
              />
              <Button
                label="Nouvel intervenant"
                onClick={() => setCreateModalOpen(true)}
              />
            </div>
          )}
        </div>
        <div className="flex flex-row gap-2 max-w-1p2k mt-4">
          {mainTabs.map((t) => (
            <button
              key={t.value}
              onClick={() => setActiveMainTab(t.value)}
              className={`px-4 py-2 rounded-xl text-h3 font-medium border transition-all ${
                activeMainTab === t.value
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 min-h-0 px-4 lg:px-10 pb-6 flex flex-col w-full">
        {activeMainTab === "charge" && orgData?.id && (
          <div className="max-w-1k w-full flex-1 min-h-0 flex flex-col">
            <ChargeArtisanTable
              orgId={orgData.id}
              initialNameFilter={chargeInitialFilter}
            />
          </div>
        )}
        {activeMainTab === "list" && table && (
          <div className="flex-1 min-h-0 max-w-1p2k w-full">
            <DataGrid
              selectable={true}
              table={table}
              hide_lines={true}
              tabs={typeTabs}
              activeTab={activeTab}
              onTabChange={(tab) => setActiveTab(tab)}
              onClick={handleRowClick}
              handleSelectionModelChange={(ids) => setSelectedIds(ids)}
              onSearch={orgData?.id ? (q) => searchPartnersFull(q, orgData.id!) : undefined}
              onSearchSelect={handleSearchSelect}
              hasNext={hasMorePartnersList}
              hasPrev={hasPrevPartnersList}
              onNext={() => orgData?.id && fetchNextPagePartners(orgData.id)}
              onPrev={() => orgData?.id && fetchPrevPagePartners(orgData.id)}
              handleSortChange={(model) => {
                const validSortFields = ["company_name", "display_name", "email", "phone_number", "decennales_count"];
                if (orgData?.id && model.length > 0 && validSortFields.includes(model[0].field))
                  setPartnersSort(orgData.id, model[0].field, model[0].sort);
              }}
              storageKey="partners"
            />
          </div>
        )}
      </div>
    </div>
  );
}
